<script setup lang="ts">
import HeroHome from '@/components/heroes/HeroHome.vue'

import IOHeroContentDefault from '@/components/io/io-hero/content/IOHeroContentDefault.vue'
import IOHeroContentMenuBuffer from '@/components/io/io-hero/content/IOHeroContentMenuBuffer.vue'
import IOHeroOverlays from '@/components/io/io-hero/layers/IOHeroOverlays.vue'

import { mobileMenuSnapPoint } from '@/io'
const { $io } = useNuxtApp()

const { $viewport } = useNuxtApp()
const w = computed(() => $viewport?.w ? $viewport.w.value : 0)

const hero = computed(() => $io.hero)

const route = useRoute()
const routeName = computed(() => String(route.name))
const routeSlug = computed(() => String(route.path).split('/').pop())

const loaded = ref(false)
onMounted(() => {
  nextTick(() => {
    loaded.value = true
  })
})

const heroes = {
  "HomeHero": HeroHome,
}

const baseClass = computed(() => hero.value.base?.class)

const autoHeightActive = computed(() => hero.value.autoHeight)
const autoHeightSettings = computed(() => hero.value.autoHeightSettings)


const componentName = computed(() => hero.value.content?.component || '')
const isComponent = computed(() => componentName.value.length > 0)



// MEDIA
const mediaType = computed(() => hero.value.media?.type)
const mediaSrc = computed(() => hero.value.media?.media?.src)
const mediaVideoId = computed(() => hero.value.media?.media?.vid)
const mediaVideoIdVimeo = computed(() => hero.value.media?.media?.vid)
const mediaVideoIdYoutube = computed(() => hero.value.media?.media?.vid)

const mediaVideoSrc = computed(() => {
  const id = encodeURIComponent(mediaVideoId.value)
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&loop=1&playlist=${id}&iv_load_policy=3`
})

const mediaVideoSrcVimeo = computed(() => {
  const id = encodeURIComponent(mediaVideoIdVimeo.value)
  return `https://player.vimeo.com/video/${id}?background=1&autoplay=1&muted=1&loop=1&title=0&byline=0&portrait=0`
})

const mediaVideoSrcYoutube = computed(() => {
  const id = encodeURIComponent(mediaVideoIdYoutube.value)
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&loop=1&playlist=${id}&iv_load_policy=3`
})

  // const vimeoVideoId = computed(() => {
//   return hero.value?.media?.videoIdVimeo || ''
// })

// const youtubeVideoId = computed(() => {
//   return hero.value?.media?.videoIdYoutube || ''
// })

// const youtubeSrc = computed(() => {
//   const id = encodeURIComponent(youtubeVideoId.value)
//   return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&loop=1&playlist=${id}&iv_load_policy=3`
// })

// const vimeoSrc = computed(() => {
//   const id = encodeURIComponent(vimeoVideoId.value)
//   return `https://player.vimeo.com/video/${id}?background=1&autoplay=1&muted=1&loop=1&title=0&byline=0&portrait=0`
// })

// Content
const contentModel = computed(() => hero.value.content?.model || "page")
const content = computed(() => hero.value.content?.content)

const heroKey = computed(() => hero.value.key)

const overlaysAvailable = computed(() => hero.value.overlays?.overlays?.length > 0)


onMounted(() => {
  console.log('HERO', hero.value)
})

const isProductPage = computed(() => String(route.name).includes('products-'))
</script>

<template>
  <div v-if="hero" class="io-hero overflow-hidden" :class="[routeName, baseClass, { 'product-page': isProductPage }]" :style="{
    minHeight: autoHeightSettings.minHeight,
    maxHeight: autoHeightSettings.maxHeight,
  }">


    <slot name="pre-hero" />

    <div class="hero-container">
      

      <slot name="pre-content" />

      <!-- ── MEDIA LAYER ── -->
      <div v-if="loaded" class="layer media flex flex-col" :class="[routeName]">
        <slot name="pre-media-layer" />
        <transition name="fade-slow" mode="out-in">
          <!-- IMAGE CASE -->
          <div v-if="mediaSrc && hero.media.type === 'image'" key="img" class="w-full h-full ">
            <transition name="fade-slow" mode="out-in">
              <img :src="mediaSrc" 
                :key="mediaSrc + routeName" class="w-full h-full object-cover object-center pointer-events-none" />
            </transition>
          </div>
          <!-- VIDEO Vimeo -->
           <!-- v-else-if="hero.media.type === 'video-vimeo'" -->
          <div v-else-if="hero.media.type === 'video'" :key="'vimeo-' + mediaVideoIdVimeo" class="sec-video-vimeo w-full h-full">
            <client-only>
              <iframe
                  v-if="hero.media.type === 'video' && mediaVideoIdVimeo"
                  :src="mediaVideoSrcVimeo"
                  frameborder="0"
                  allow="autoplay; fullscreen; muted"
                  allowfullscreen
                  loading="lazy"
                  class="absolute inset-0 w-full h-full object-cover pointer-events-none"
                ></iframe>
            </client-only>
          </div> 

          <!-- VIDEO YOUTUBE -->
          <!-- v-else-if="hero.media.type === 'video-youtube'" -->
          <div v-else-if="hero.media.type === 'video'" :key="'youtube-' + mediaVideoIdYoutube" class="sec-video-youtube w-full h-full">
            <client-only>
                <iframe
                  v-if="hero.media.type === 'video' && mediaVideoIdYoutube"
                  :src="mediaVideoSrc"
                  frameborder="0"
                  allow="autoplay; fullscreen; muted"
                  allowfullscreen
                  loading="lazy"
                  class="inset-0 w-full h-full object-cover pointer-events-none"
                ></iframe>
            </client-only>
          </div>
        </transition>
        <slot name="post-media-layer" />
      </div>

      <!-- ── OVERLAY LAYER ── -->

      <IOHeroOverlays v-if="loaded && overlaysAvailable" :overlays="hero.overlays" :content-model="contentModel" class="layer overlay" />

      <!-- ── CONTENT LAYER ── -->
      <!-- <transition name="fade" mode="out-in" appear>
        <div v-if="loaded && isComponent" class="layer content component h-full relative z-30 flex flex-col items-center justify-center" :class="routeName">
          <component :is="heroes[componentName as keyof typeof heroes]" :id="routeName" :content="hero.content" />
        </div>
        <div v-else-if="loaded && !isComponent" class="layer content dynamic h-full relative z-30 flex flex-col items-center justify-center" :class="routeName">
          <div class="content-model page w-full h-full flex items-center justify-center -translate-y-16px" >
            <IOHeroContentDefault :id="routeName" :key="routeName" :content="content" />
          </div>
        </div>
      </transition> -->
      <transition name="fade" mode="out-in" appear>
        <div v-if="loaded && isComponent" class="layer content component h-full relative z-30 flex flex-col items-center justify-center" :class="routeName">
          <component :is="heroes[componentName as keyof typeof heroes]" :id="routeName" :content="hero.content" />
        </div>
        <div v-else-if="loaded && !isComponent" class="layer content dynamic h-full relative z-30 flex flex-col items-center justify-center" :class="routeName">
          <div class="content-model page w-full h-full flex items-center justify-center -translate-y-16px" >
            <IOHeroContentDefault :id="routeName" :key="routeName" :content="content" />
          </div>
        </div>
      </transition>


      <slot name="dividers" />

      <slot name="post-content" />


    </div> <!-- hero-container -->

    <slot name="post-hero" />

  </div>
  <div v-else class="io-hero-default w-full h-64 bg-gray-200"></div>
</template>



<style lang="scss" scoped>

.sec-video-vimeo {
  iframe {

    width: 300%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @include mq($until: d) {
      aspect-ratio: 16 / 9;
    }

    @include mq($from: d) {
      aspect-ratio: auto;
      width: 100%;
      height: 150%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
}
.sec-video-youtube {


  iframe {
    // width: 100%;
    // height: 100%;
    // height: 200%;
    width: 300%;
    // height: 100%;
    
    // left: 50%;
    // top: 50%;
    // transform: translate(-50%, -50%);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @include mq($until: d) {
      aspect-ratio: 16 / 9;
    }


    @include mq($from: d) {
      aspect-ratio: auto;
      width: 100%;
      height: 150%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
}


.io-hero {
  position: relative;
  width: 100%;
  height: 100%;

  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  transition: all 0.75s ease-in-out;

  // If we want curved edges, we can add this:
  border-radius: 0 0 60px 60px;

  min-height: 600px;

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    transition: all 0.28s ease-in-out;
    padding-top: 0;

    &.content {
      position: relative;
      transition: all 0.75s ease-in-out;

    }

    &.info {
      padding-top: 100px;

      @include mq($from: d) {
        padding-top: 120px;
      }
    }


    &.media {
      background: var(--e-light-blue);

      .media-image {
        width: 100%; // Ensure container fills parent
        height: 100%; // Ensure container fills parent
        display: flex; // Optional: if you want to use flex centering

        >img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center; // This centers the image within its container
        }

        &.index {

          >img {
            object-position: 60% !important;
          }

          // object-position: start !important;

          // @include mq($from: t) {
          //   object-position: center;
          // }

        }

        &.blog {
          >img {
            width: 100%;
            object-position: center !important;
          }
        }

        &.servicepage {

          >.sec-layer {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to right, var(--e-light-blue), var(--e-purple));
            mix-blend-mode: multiply;

            display: none;
          }

          >img {
            width: 100%;
            object-position: center !important;
          }
        }
      }
    }

    &.overlay {
    
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

    }
  }

  &.index {
    @include mq($from: dm) {
      min-height: 600px !important;
    }
  }
}

</style>